import { useEffect, useState } from "react";
import { Plus, Trash2, FlaskConical, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { api } from "@/lib/api";
import { formatDateEU, todayISO } from "@/lib/constants";

const blank = () => ({
  test_date: todayISO(),
  product: "Salsa Verde Habanero",
  result: "OK",
  batch_id: "",
  notes: "",
  created_by: "",
});

export default function LabTests() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());

  const load = async () => {
    const [l, m] = await Promise.all([api.listLabTests(), api.getMeta()]);
    setItems(l);
    setMeta(m);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.product) return toast.error("Product is required");
    try {
      await api.createLabTest(form);
      toast.success("Lab test logged");
      setOpen(false);
      setForm(blank());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this test?")) return;
    try { await api.deleteLabTest(id); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="page-lab-tests">
      <PageHeader
        overline="Omavalvonta · Food Safety"
        title="Lab Tests"
        subtitle="Quick result log — OK / Not OK per batch or product"
        actions={
          <Button onClick={() => { setForm(blank()); setOpen(true); }} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="add-lab-test">
            <Plus size={16} className="mr-2" /> New Test
          </Button>
        }
      />

      {items.length === 0 ? (
        <EmptyState icon={FlaskConical} title="No lab tests logged" description="Add lab results here for traceability." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((i) => {
            const ok = i.result === "OK";
            return (
              <div key={i.id} className="card-soft p-5 flex flex-col gap-2" data-testid={`lab-card-${i.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#6C757D] font-bold">{formatDateEU(i.test_date)}</div>
                    <div className="font-display font-bold text-lg text-[#222]">{i.product}</div>
                    {i.batch_id && <div className="font-mono text-xs text-[#E85D04]">{i.batch_id}</div>}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => del(i.id)} className="text-[#E76F51] hover:bg-[#E76F51]/10">
                    <Trash2 size={16} />
                  </Button>
                </div>
                <div className={`rounded-md px-2.5 py-1.5 text-xs font-semibold flex items-center gap-1.5 w-fit ${ok ? "bg-[#1F4D3A]/10 text-[#1F4D3A]" : "bg-[#D62828]/10 text-[#D62828]"}`}>
                  {ok ? <Check size={14} /> : <X size={14} />} {i.result}
                </div>
                {i.notes && <p className="text-xs text-[#222]/80 line-clamp-2">{i.notes}</p>}
                {i.created_by && <div className="text-[10px] text-[#6C757D]">By {i.created_by}</div>}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="lab-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">New Lab Test</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Date">
              <Input type="date" value={form.test_date} onChange={(e) => setForm({ ...form, test_date: e.target.value })} data-testid="lab-date" />
            </Field>
            <Field label="Product *">
              {meta?.batchable_products?.length ? (
                <Select value={form.product} onValueChange={(v) => setForm({ ...form, product: v })}>
                  <SelectTrigger data-testid="lab-product"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {meta.batchable_products.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} data-testid="lab-product" />
              )}
            </Field>
            <Field label="Batch ID (optional)">
              <Input value={form.batch_id} onChange={(e) => setForm({ ...form, batch_id: e.target.value })} placeholder="e.g. VH-20260622-01" className="font-mono" data-testid="lab-batch" />
            </Field>
            <Field label="Result">
              <div className="flex gap-2">
                <Button type="button" variant={form.result === "OK" ? "default" : "outline"}
                  className={form.result === "OK" ? "bg-[#1F4D3A] hover:bg-[#173629] text-white flex-1" : "flex-1"}
                  onClick={() => setForm({ ...form, result: "OK" })} data-testid="lab-result-ok">OK</Button>
                <Button type="button" variant={form.result === "Not OK" ? "default" : "outline"}
                  className={form.result === "Not OK" ? "bg-[#D62828] hover:bg-[#a51e1e] text-white flex-1" : "flex-1"}
                  onClick={() => setForm({ ...form, result: "Not OK" })} data-testid="lab-result-not-ok">Not OK</Button>
              </div>
            </Field>
            <Field label="Notes">
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="lab-notes" />
            </Field>
            <Field label="By">
              <Input value={form.created_by} onChange={(e) => setForm({ ...form, created_by: e.target.value })} placeholder="Name" data-testid="lab-by" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="save-lab">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <Label className="text-xs uppercase tracking-wider text-[#1F4D3A] font-semibold">{label}</Label>
    {children}
  </div>
);
